import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Archive, ArrowLeft, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCanExport } from "@/hooks/useCanExport";
import { useExportDownload } from "@/hooks/useExportDownload";
import type { ExportSummary } from "@/model/interface/DataExport";
import { useCreateExportMutation, useGetExportsQuery, useGetHomeQuery } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import { formatDateTime } from "@/util/date";

const PAGE_SIZE = 10;
const POLL_INTERVAL_MS = 3000;
const QUEUED_POLL_INTERVAL_MS = 60000;

function statusPill(exportSummary: ExportSummary): { label: string; className: string } {
  if (exportSummary.status === "PENDING") {
    return { label: "Queued", className: "status-pill-neutral" };
  }
  if (exportSummary.status === "RUNNING") {
    return { label: "Exporting", className: "status-pill-info" };
  }
  if (exportSummary.status === "FAILED") {
    return { label: "Failed", className: "status-pill-destructive" };
  }
  if (!exportSummary.downloadable) {
    return { label: "Expired", className: "status-pill-neutral" };
  }
  return { label: "Ready", className: "status-pill-success" };
}

function exportMeta(exportSummary: ExportSummary): string {
  return [
    exportSummary.requestedByName,
    formatDateTime(exportSummary.createdAt),
    exportSummary.status === "COMPLETED" ? `${exportSummary.rowCount.toLocaleString()} rows` : null,
    exportSummary.downloadable && exportSummary.expiresAt ? `available until ${formatDateTime(exportSummary.expiresAt)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export default function ExportPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pollingInterval, setPollingInterval] = useState(0);
  const [confirmWorkspaceOpen, setConfirmWorkspaceOpen] = useState(false);
  const { isOwner } = useCanExport();
  const { data: user } = useGetHomeQuery();
  const { data, isLoading } = useGetExportsQuery({ page, size: PAGE_SIZE }, { pollingInterval });
  const [createExport, { isLoading: isStartingWorkspace }] = useCreateExportMutation();
  const { downloadExport, downloading } = useExportDownload();

  const exports = data?.items ?? [];
  const hasActiveExport = exports.some((exportSummary) => exportSummary.status === "PENDING" || exportSummary.status === "RUNNING");
  const hasRunningExport = exports.some((exportSummary) => exportSummary.status === "RUNNING");
  const exportAvailableUntil = user?.activeOrganisation?.exportAvailableUntil;

  async function startWorkspaceExport() {
    try {
      await createExport({ kind: "WORKSPACE" }).unwrap();
      toast.success("Workspace export queued. It runs at the top of the hour, and you get a notification and an email when it is ready.");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "The workspace export could not start"));
    }
  }

  useEffect(() => {
    setPollingInterval(hasRunningExport ? POLL_INTERVAL_MS : hasActiveExport ? QUEUED_POLL_INTERVAL_MS : 0);
  }, [hasActiveExport, hasRunningExport]);

  return (
    <div className="p-4 text-foreground">
      <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate("/manage-organisation/settings")}>
        <ArrowLeft className="mr-1 h-4 w-4" />
        Back to General
      </Button>

      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Export</h1>
        <p className="text-sm text-muted-foreground">
          Download your workspace data. Download links work for 7 days after an export finishes.
        </p>
      </div>

      {isOwner && (
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Export workspace</CardTitle>
            <CardDescription>
              A zip with every lead, contact, property, deal, task, note, list, tag, Do Not Contact entry, consent
              record, call and message, plus call recordings, vCons and uploaded files.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {exportAvailableUntil && (
              <p className="text-sm text-warning">
                Your subscription has ended. You can export the workspace until {formatDateTime(exportAvailableUntil)}.
              </p>
            )}
            <Button onClick={() => setConfirmWorkspaceOpen(true)} disabled={isStartingWorkspace}>
              {isStartingWorkspace ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Archive className="mr-2 h-4 w-4" />}
              Export workspace
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className={isOwner ? "mt-4 max-w-2xl" : "max-w-2xl"}>
        <CardHeader>
          <CardTitle>Export history</CardTitle>
          <CardDescription>
            {isOwner ? "Every export made in this workspace." : "Your exports."} Leads, contacts, lists, properties,
            deals and Do Not Contact are exported from their own pages.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && <Skeleton className="h-24 w-full" />}

          {!isLoading && exports.length === 0 && <p className="text-sm text-muted-foreground">No exports yet.</p>}

          {!isLoading && exports.length > 0 && (
            <div className="divide-y divide-border rounded-[var(--radius)] border border-border">
              {exports.map((exportSummary) => {
                const pill = statusPill(exportSummary);
                return (
                  <div key={exportSummary.id} className="flex flex-wrap items-center gap-3 p-3">
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium">{exportSummary.label ?? "Export"}</span>
                        <span className={pill.className}>{pill.label}</span>
                      </div>
                      <div className="text-xs tabular-nums text-muted-foreground">{exportMeta(exportSummary)}</div>
                      {exportSummary.status === "FAILED" && exportSummary.errorSummary && (
                        <div className="text-xs text-destructive">{exportSummary.errorSummary}</div>
                      )}
                    </div>
                    {exportSummary.downloadable && (
                      <Button variant="outline" size="sm" onClick={() => downloadExport(exportSummary.id)} disabled={downloading}>
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {data.page + 1} of {data.totalPages}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={data.page === 0} onClick={() => setPage((current) => current - 1)}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={data.last} onClick={() => setPage((current) => current + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmWorkspaceOpen} onOpenChange={setConfirmWorkspaceOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Export the whole workspace?</AlertDialogTitle>
            <AlertDialogDescription>
              This builds one zip with every lead, contact, property, deal, task, note, list, tag, Do Not Contact entry,
              consent record, call and message, plus call recordings, vCons and uploaded files. It runs at the top of
              the hour, and large workspaces can take a while after that. You get a notification and an email when it
              is ready, and the download link works for 7 days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void startWorkspaceExport()}>Export workspace</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
